import React, { useEffect, useMemo, useState } from "react";
import { updateProfile } from "firebase/auth";
import { db } from "../services/firebase";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";
import { getTrialStatus } from "../utils/trialStatus";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "preferences", label: "Preferences" },
  { id: "notifications", label: "Notifications" },
  { id: "billing", label: "Billing" },
  { id: "account", label: "Account" },
];

const DEFAULT_PREFERENCES = {
  useImperial: true,
  learnMode: false,
  darkTheme: true,
  hideWalkthrough: false,
};

const BILLING_PORTAL_URL =
  "https://us-central1-manucalc-1d2b5.cloudfunctions.net/createBillingPortalSession";

function formatDate(value) {
  if (!value) return "N/A";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleDateString();
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString();
  } catch {
    return "N/A";
  }
}

function formatDateTime(value) {
  if (!value) return "N/A";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString();
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
  } catch {
    return "N/A";
  }
}

function normalizePreferences(userData) {
  return {
    useImperial:
      userData?.preferences?.useImperial ?? DEFAULT_PREFERENCES.useImperial,
    learnMode:
      userData?.preferences?.learnMode ?? DEFAULT_PREFERENCES.learnMode,
    darkTheme: true,
    hideWalkthrough:
      userData?.preferences?.hideWalkthrough ?? DEFAULT_PREFERENCES.hideWalkthrough,
  };
}

function getDisplayStatus(subscriptionStatus, trialStatus) {
  const normalizedSubscription = (subscriptionStatus || "none").toLowerCase();

  if (trialStatus?.isTrialActive) {
    return "trial";
  }

  if (normalizedSubscription === "active") {
    return "active";
  }

  if (normalizedSubscription === "past_due") {
    return "past due";
  }

  if (normalizedSubscription === "checkout_started") {
    return "checkout started";
  }

  if (normalizedSubscription === "canceled") {
    return "canceled";
  }

  if (normalizedSubscription === "unpaid") {
    return "unpaid";
  }

  if (trialStatus?.isTrialExpired) {
    return "trial expired";
  }

  return normalizedSubscription;
}

function getSubscriptionStatusLabel(subscriptionStatus, trialStatus) {
  const normalizedSubscription = (subscriptionStatus || "none").toLowerCase();

  if (trialStatus?.isTrialActive) {
    return "trial";
  }

  if (normalizedSubscription === "trialing") {
    return "trial";
  }

  if (normalizedSubscription === "active") {
    return "active";
  }

  return normalizedSubscription;
}

function StatusBadge({ status }) {
  const normalized = (status || "inactive").toLowerCase();

  const isPositive =
    normalized === "active" ||
    normalized === "trial" ||
    normalized === "trialing";
  const isWarning =
    normalized === "past due" ||
    normalized === "past_due" ||
    normalized === "checkout started" ||
    normalized === "checkout_started";
  const label = normalized.replace(/_/g, " ");

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "capitalize",
        background: isPositive
          ? "rgba(34,197,94,0.15)"
          : isWarning
          ? "rgba(245,158,11,0.15)"
          : "rgba(239,68,68,0.15)",
        color: isPositive
          ? "#22c55e"
          : isWarning
          ? "#f59e0b"
          : "#ef4444",
        border: "1px solid var(--border-color)",
      }}
    >
      {label}
    </span>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <div
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-color)",
        borderRadius: "16px",
        padding: "20px",
        boxShadow: "var(--shadow-card)",
      }}
    >
      <div style={{ marginBottom: "16px" }}>
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 700,
            color: "var(--text-primary)",
          }}
        >
          {title}
        </h3>
        {description ? (
          <p
            style={{
              margin: "8px 0 0 0",
              color: "var(--text-muted)",
              fontSize: "14px",
              lineHeight: 1.5,
            }}
          >
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: "16px",
        padding: "12px 0",
        borderBottom: "1px solid var(--border-color)",
      }}
    >
      <span style={{ color: "var(--text-muted)", fontSize: "14px" }}>
        {label}
      </span>
      <span
        style={{
          color: "var(--text-primary)",
          fontSize: "14px",
          fontWeight: 600,
          textAlign: "right",
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
}

export default function Settings({
  user,
  preferences,
  setPreferences,
}) {
  const [activeTab, setActiveTab] = useState("billing");
  const [billingData, setBillingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [billingPortalError, setBillingPortalError] = useState("");
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState("");

  useEffect(() => {
    if (!user?.uid) {
      setBillingData(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const userRef = doc(db, "users", user.uid);

    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        const userData = docSnap.exists() ? docSnap.data() : null;

        setBillingData(userData);

        setDisplayName(
          userData?.displayName || user?.displayName || user?.email?.split("@")[0] || ""
        );

        if (setPreferences) {
          setPreferences(normalizePreferences(userData));
        }

        setLoading(false);
      },
      (error) => {
        console.error("Realtime billing error:", error);
        setBillingData(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, setPreferences]);

  async function handleSaveProfile() {
    if (!user?.uid) return;

    const trimmedName = displayName.trim();

    if (!trimmedName) {
      setProfileMessage("Username cannot be empty.");
      return;
    }

    try {
      setSavingProfile(true);
      setProfileMessage("");

      await updateProfile(user, {
        displayName: trimmedName,
      });

      const userRef = doc(db, "users", user.uid);

      await setDoc(
        userRef,
        {
          displayName: trimmedName,
          profileUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setBillingData((prev) => ({
        ...(prev || {}),
        displayName: trimmedName,
      }));

      setProfileMessage("Username saved.");
    } catch (error) {
      console.error("Failed to save username:", error);
      setProfileMessage("Failed to save username.");
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleSavePreferences() {
    if (!user?.uid) return;

    try {
      setSavingPreferences(true);
      setSaveMessage("");

      const userRef = doc(db, "users", user.uid);

      await setDoc(
        userRef,
        {
          preferences: {
            useImperial: preferences.useImperial,
            learnMode: preferences.learnMode,
            darkTheme: true,
            hideWalkthrough: preferences.hideWalkthrough ?? false,
          },
          preferencesUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const freshSnap = await getDoc(userRef);
      const freshData = freshSnap.exists() ? freshSnap.data() : null;

      setBillingData(freshData);

      if (setPreferences) {
        setPreferences(normalizePreferences(freshData));
      }

      setSaveMessage("Preferences saved.");
    } catch (error) {
      console.error("Failed to save preferences:", error);
      setSaveMessage("Failed to save preferences.");
    } finally {
      setSavingPreferences(false);
    }
  }

  async function handleManageBilling() {
    if (!user?.uid) {
      setBillingPortalError("No user found for billing portal.");
      return;
    }

    try {
      setBillingPortalLoading(true);
      setBillingPortalError("");

      const response = await fetch(BILLING_PORTAL_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uid: user.uid,
          returnBaseUrl: window.location.origin,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to open billing portal");
      }

      if (!data?.url) {
        throw new Error("No billing portal URL returned");
      }

      window.location.href = data.url;
    } catch (error) {
      console.error("Billing portal error:", error);
      setBillingPortalError(
        error?.message || "Failed to open billing portal."
      );
    } finally {
      setBillingPortalLoading(false);
    }
  }

  async function handleRefreshBilling() {
    if (!user?.uid) return;

    try {
      setLoading(true);
      setBillingPortalError("");

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : null;

      setBillingData(userData);

      if (setPreferences) {
        setPreferences(normalizePreferences(userData));
      }
    } catch (error) {
      console.error("Failed to refresh billing data:", error);
      setBillingPortalError("Failed to refresh billing data.");
    } finally {
      setLoading(false);
    }
  }

  const trialStatus = useMemo(() => {
    return getTrialStatus(
      billingData?.trialStartDate,
      billingData?.trialEndDate
    );
  }, [billingData]);

  const displayStatus = useMemo(() => {
    return getDisplayStatus(
      billingData?.subscriptionStatus,
      trialStatus
    );
  }, [billingData, trialStatus]);

  const displayedSubscriptionStatus = useMemo(() => {
    return getSubscriptionStatusLabel(
      billingData?.subscriptionStatus,
      trialStatus
    );
  }, [billingData, trialStatus]);

  const trialBadgeStatus = useMemo(() => {
    if (!billingData?.trialStartDate && !billingData?.trialEndDate) {
      return "none";
    }

    return trialStatus?.isTrialActive ? "trial active" : "trial expired";
  }, [billingData, trialStatus]);

  const trialDetailText = useMemo(() => {
    if (!billingData?.trialStartDate && !billingData?.trialEndDate) {
      return "N/A";
    }

    if (trialStatus?.isTrialActive) {
      return trialStatus.formattedTimeRemaining || "Active";
    }

    if (trialStatus?.isTrialExpired) {
      return "Expired";
    }

    return "N/A";
  }, [billingData, trialStatus]);

  const planNameText = useMemo(() => {
    if (billingData?.planName) {
      return billingData.planName;
    }

    if (trialStatus?.isTrialActive) {
      return "Trial Plan";
    }

    return "N/A";
  }, [billingData, trialStatus]);

  const renewalStatusText = useMemo(() => {
    if (billingData?.cancelAtPeriodEnd) {
      return "Cancels at period end";
    }

    if (displayedSubscriptionStatus === "active") {
      return "Renews automatically";
    }

    if (displayedSubscriptionStatus === "trial") {
      return "Starts billing after trial";
    }

    return "N/A";
  }, [billingData, displayedSubscriptionStatus]);

  const nextBillingDateText = useMemo(() => {
    if (billingData?.currentPeriodEnd) {
      return formatDate(billingData.currentPeriodEnd);
    }

    if (displayedSubscriptionStatus === "trial" && billingData?.trialEndDate) {
      return formatDate(billingData.trialEndDate);
    }

    return "N/A";
  }, [billingData, displayedSubscriptionStatus]);

  const content = useMemo(() => {
    if (loading) {
      return (
        <SectionCard
          title="Loading settings"
          description="Pulling your account and billing details."
        >
          <p style={{ margin: 0, color: "var(--text-secondary)" }}>
            Loading...
          </p>
        </SectionCard>
      );
    }

    if (activeTab === "profile") {
      return (
        <SectionCard
          title="Profile"
          description="Your basic account identity."
        >
          <label style={fieldLabel}>
            Username
            <input
              type="text"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Enter your display name"
              style={textInput}
            />
          </label>

          <Row label="Email" value={user?.email || "N/A"} />
          <Row label="User ID" value={user?.uid || "N/A"} />

          <div
            style={{
              paddingTop: "16px",
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              style={{
                ...buttonPrimary,
                opacity: savingProfile ? 0.7 : 1,
                cursor: savingProfile ? "not-allowed" : "pointer",
              }}
              onClick={handleSaveProfile}
              disabled={savingProfile}
            >
              {savingProfile ? "Saving..." : "Save Username"}
            </button>

            {profileMessage ? (
              <span style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                {profileMessage}
              </span>
            ) : null}
          </div>
        </SectionCard>
      );
    }

    if (activeTab === "preferences") {
      return (
        <SectionCard
          title="Preferences"
          description="Manage your default calculator and app behavior."
        >
          <label style={checkboxRow}>
            <input
              type="checkbox"
              checked={preferences?.useImperial ?? true}
              onChange={(e) =>
                setPreferences((prev) => ({
                  ...prev,
                  useImperial: e.target.checked,
                }))
              }
            />
            <span>Use imperial units by default</span>
          </label>

          <label style={checkboxRow}>
            <input
              type="checkbox"
              checked={preferences?.learnMode ?? false}
              onChange={(e) =>
                setPreferences((prev) => ({
                  ...prev,
                  learnMode: e.target.checked,
                }))
              }
            />
            <span>Enable learn mode</span>
          </label>

          <label style={checkboxRow}>
            <input
              type="checkbox"
              checked={!(preferences?.hideWalkthrough ?? false)}
              onChange={(e) =>
                setPreferences((prev) => ({
                  ...prev,
                  hideWalkthrough: !e.target.checked,
                }))
              }
            />
            <span>Show dashboard walkthrough</span>
          </label>

          <div
            style={{
              paddingTop: "16px",
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={handleSavePreferences}
              style={{
                ...buttonPrimary,
                opacity: savingPreferences ? 0.7 : 1,
                cursor: savingPreferences ? "not-allowed" : "pointer",
              }}
              disabled={savingPreferences}
            >
              {savingPreferences ? "Saving..." : "Save Preferences"}
            </button>

            {saveMessage ? (
              <span style={{ color: "var(--text-secondary)", fontSize: "14px" }}>
                {saveMessage}
              </span>
            ) : null}
          </div>
        </SectionCard>
      );
    }

    if (activeTab === "notifications") {
      return (
        <SectionCard
          title="Notifications"
          description="Notification settings UI shell is in place."
        >
          <label style={checkboxRow}>
            <input type="checkbox" disabled />
            <span>Email product updates</span>
          </label>
          <label style={checkboxRow}>
            <input type="checkbox" disabled />
            <span>Billing reminders</span>
          </label>
          <label style={checkboxRow}>
            <input type="checkbox" disabled />
            <span>Trial ending reminders</span>
          </label>
        </SectionCard>
      );
    }

    if (activeTab === "billing") {
      return (
        <SectionCard
          title="Billing"
          description="Live subscription and trial data pulled from Firestore."
        >
          <Row
            label="Account Access Status"
            value={<StatusBadge status={displayStatus} />}
          />
          <Row
            label="Subscription Status"
            value={<StatusBadge status={displayedSubscriptionStatus} />}
          />
          <Row label="Plan" value={planNameText} />
          <Row label="Renewal Status" value={renewalStatusText} />
          <Row label="Next Billing Date" value={nextBillingDateText} />
          <Row
            label="Trial Status"
            value={<StatusBadge status={trialBadgeStatus} />}
          />
          <Row
            label="Trial Time Remaining"
            value={trialDetailText}
          />
          <Row
            label="Trial Start"
            value={formatDate(billingData?.trialStartDate)}
          />
          <Row
            label="Trial End"
            value={formatDate(billingData?.trialEndDate)}
          />
          <Row
            label="Current Period End"
            value={formatDate(billingData?.currentPeriodEnd)}
          />
          <Row
            label="Cancel At Period End"
            value={billingData?.cancelAtPeriodEnd ? "Yes" : "No"}
          />
          <Row
            label="Last Synced"
            value={formatDateTime(billingData?.updatedAt)}
          />
          <Row
            label="Stripe Customer ID"
            value={billingData?.stripeCustomerId || "N/A"}
          />
          <Row
            label="Subscription ID"
            value={billingData?.stripeSubscriptionId || "N/A"}
          />

          {billingPortalError ? (
            <div
              style={{
                marginTop: "16px",
                padding: "12px 14px",
                borderRadius: "12px",
                background: "rgba(239,68,68,0.12)",
                border: "1px solid rgba(239,68,68,0.22)",
                color: "#ef4444",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {billingPortalError}
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
              paddingTop: "16px",
            }}
          >
            <button
              type="button"
              style={{
                ...buttonPrimary,
                opacity: billingPortalLoading ? 0.7 : 1,
                cursor: billingPortalLoading ? "not-allowed" : "pointer",
              }}
              onClick={handleManageBilling}
              disabled={billingPortalLoading}
            >
              {billingPortalLoading ? "Opening..." : "Manage Billing"}
            </button>

            <button
              type="button"
              style={buttonSecondary}
              onClick={handleRefreshBilling}
            >
              Refresh
            </button>
          </div>
        </SectionCard>
      );
    }

    return (
      <SectionCard
        title="Account"
        description="Account actions will be wired up in later steps."
      >
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <button type="button" style={buttonSecondary}>
            Change Password
          </button>
          <button type="button" style={buttonSecondary}>
            Enable 2FA
          </button>
          <button type="button" style={buttonDanger}>
            Delete Account
          </button>
        </div>
      </SectionCard>
    );
    // Tab content callbacks are stable enough for this settings surface; adding them here causes needless tab recomputation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    billingData,
    displayName,
    billingPortalError,
    billingPortalLoading,
    displayStatus,
    displayedSubscriptionStatus,
    loading,
    nextBillingDateText,
    profileMessage,
    savingProfile,
    planNameText,
    preferences,
    renewalStatusText,
    saveMessage,
    savingPreferences,
    setPreferences,
    trialBadgeStatus,
    trialDetailText,
    user,
  ]);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "transparent",
        color: "var(--text-primary)",
        padding: "32px 20px",
        transition: "color 0.25s ease",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{
              margin: 0,
              fontSize: "32px",
              fontWeight: 800,
              letterSpacing: "-0.02em",
              color: "var(--text-primary)",
            }}
          >
            Settings
          </h1>
          <p
            style={{
              margin: "8px 0 0 0",
              color: "var(--text-muted)",
              fontSize: "15px",
            }}
          >
            Manage your account, billing, preferences, and notifications.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "260px minmax(0, 1fr)",
            gap: "24px",
            alignItems: "start",
          }}
        >
          <aside
            style={{
              background: "var(--bg-surface)",
              border: "1px solid var(--border-color)",
              borderRadius: "16px",
              padding: "12px",
              boxShadow: "var(--shadow-card)",
            }}
          >
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    ...buttonTab,
                    background: isActive ? "var(--accent)" : "transparent",
                    color: isActive ? "#ffffff" : "var(--text-secondary)",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </aside>

          <main>{content}</main>
        </div>
      </div>
    </div>
  );
}

const buttonBase = {
  border: "none",
  borderRadius: "12px",
  padding: "10px 14px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
  transition: "0.2s ease",
};

const buttonPrimary = {
  ...buttonBase,
  background: "var(--accent)",
  color: "#fff",
};

const buttonSecondary = {
  ...buttonBase,
  background: "var(--bg-surface-alt)",
  color: "var(--text-primary)",
  border: "1px solid var(--border-color)",
};

const buttonDanger = {
  ...buttonBase,
  background: "rgba(239,68,68,0.15)",
  color: "#ef4444",
  border: "1px solid rgba(239,68,68,0.28)",
};

const buttonTab = {
  ...buttonBase,
  width: "100%",
  textAlign: "left",
  marginBottom: "8px",
};

const fieldLabel = {
  display: "grid",
  gap: "8px",
  marginBottom: "16px",
  color: "var(--text-secondary)",
  fontSize: "14px",
  fontWeight: 700,
};

const textInput = {
  width: "100%",
  border: "1px solid var(--border-color)",
  borderRadius: "12px",
  padding: "12px 14px",
  background: "var(--bg-surface-alt)",
  color: "var(--text-primary)",
  fontSize: "15px",
  outline: "none",
};

const checkboxRow = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "10px 0",
  color: "var(--text-secondary)",
  borderBottom: "1px solid var(--border-color)",
};
